import { useCallback, useEffect, useState } from 'react';

export interface OpenCloseStateResult<T> {
  isOpened: boolean;
  open(passedArgs?: T): void;
  close(resetArgs?: boolean): void;
  toggle(): void;
  args: T;
}

export function useOpenCloseState<T>(defaultState: boolean): OpenCloseStateResult<T> {
  const [isOpened, setIsOpened] = useState(defaultState);
  const [args, setArgs] = useState<T>(null as T);

  useEffect(() => {
    if (!isOpened) {
      setArgs(null as T);
    }
  }, [isOpened]);

  const open = useCallback((passedArgs?: T): void => {
    setIsOpened(true);
    setArgs(passedArgs ?? (null as T));
  }, []);

  const close = useCallback((resetArgs?: boolean) => {
    setIsOpened(false);

    if (resetArgs) setArgs(null as T);
  }, []);

  const toggle = useCallback(() => {
    if (isOpened) {
      close();
    } else {
      open();
    }
  }, [close, isOpened, open]);

  return {
    isOpened,
    open,
    close,
    toggle,
    args,
  };
}
